import { scrambleId, unscrambleId } from './id-scramble';

/**
 * URL codec for the global product id. `products.id` is a counter shared by every
 * company, so a raw `/product-account/2458/edit` would let a customer read the
 * platform's product volume. Customers see their per-company `product_number`
 * on screen; the address bar carries this opaque `p…` token instead of the id.
 *
 * Backward compatible: a legacy numeric link (bookmarks, emails) still resolves.
 * Router-free on purpose (route modules import it; tests mock the router).
 */
const PRODUCT_TOKEN_PREFIX = 'p';

export function encodeProductId(id: number): string {
  return PRODUCT_TOKEN_PREFIX + scrambleId(id);
}

/** Decode a URL product-id value back to the numeric id. A number or numeric
 *  string (legacy link) passes through; a `p…` token is unscrambled; anything
 *  else → null. */
export function decodeProductId(raw: string | number): number | null {
  if (typeof raw === 'number') return Number.isInteger(raw) ? raw : null;
  const match = /^p([0-9a-z]+)$/.exec(raw);
  if (match) return unscrambleId(match[1]!);
  const asNum = Number(raw);
  return raw !== '' && Number.isInteger(asNum) ? asNum : null;
}

/** `params: { parse, stringify }` for a `$productId` path segment the page reads
 *  as a STRING (it does `Number(productId)` itself). The page still receives the
 *  numeric id as a string; only the address bar shows the token. An undecodable
 *  value passes through unchanged so the page's own "not found" handling runs. */
export const productIdPathParams = {
  parse: <P extends { productId: string }>(params: P): P => {
    const id = decodeProductId(params.productId);
    return { ...params, productId: id === null ? params.productId : String(id) };
  },
  stringify: <P extends { productId: string }>(params: P): P => {
    const id = Number(params.productId);
    return {
      ...params,
      productId: Number.isInteger(id) && id > 0 ? encodeProductId(id) : params.productId,
    };
  },
};
